import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import GoogleIcon from '@mui/icons-material/Google'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  buildGoogleBusinessAuthUrl,
  connectGoogleBusiness,
  disconnectGoogleBusiness,
  getGoogleBusinessClientId,
  getGoogleBusinessConfig,
  getGoogleBusinessConnection,
  getGoogleBusinessLocations,
  getGoogleBusinessRedirectUri,
  saveGoogleBusinessConfig,
  selectGoogleBusinessLocation,
} from './googleBusinessService'

const INDIGO = '#4338ca'

// Default (owner) service bundle — the clinic-admin endpoints backing the full flow. Support
// surfaces inject a paste-only bundle ({ getConfig, saveConfig }) and pass showConnectFlow={false}.
// Keep this a module-level constant so it's a stable reference across renders (it's an effect dep).
const OWNER_SERVICES = {
  getConfig: getGoogleBusinessConfig,
  saveConfig: saveGoogleBusinessConfig,
  getConnection: getGoogleBusinessConnection,
  connect: connectGoogleBusiness,
  getLocations: getGoogleBusinessLocations,
  selectLocation: selectGoogleBusinessLocation,
  disconnect: disconnectGoogleBusiness,
}

// Google Business config for a clinic brand. Two flows:
//  - Connect (OAuth): pull the location + review link from Google Business Profile.
//  - Paste: enter the Google review link (or a Place ID we build the link from).
// Mounted in the Clinic Owner portal, the Practitioner owner tab, clinic-brand onboarding and the
// support Integrations page. Callers may inject a `services` bundle (defaults to the owner
// endpoints) and hide the OAuth connect flow via `showConnectFlow={false}` — support is paste-only,
// since staff can't complete the clinic's Google consent.
export default function GoogleBusinessSettings({
  clinicBrandId,
  sx = {},
  services = OWNER_SERVICES,
  showConnectFlow = true,
}) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [reviewUrl, setReviewUrl] = useState('')
  const [placeId, setPlaceId] = useState('')

  const [connected, setConnected] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [locations, setLocations] = useState([])
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [savingLocation, setSavingLocation] = useState(false)
  const popupRef = useRef(null)

  const loadLocations = useCallback(async () => {
    if (!clinicBrandId) return
    setLoadingLocations(true)
    const res = await services.getLocations(clinicBrandId)
    setLoadingLocations(false)
    if (res?.ok) {
      setLocations(res.locations || [])
      if (!(res.locations || []).length) {
        toast.info('No Google Business locations were found on this account.')
      }
    } else {
      toast.error(res?.message || 'Unable to load Google locations.')
    }
  }, [clinicBrandId, services])

  useEffect(() => {
    let active = true
    if (!clinicBrandId) return undefined
    setLoading(true)
    // Support (paste-only) has no connection endpoint — skip it and treat as never connected.
    const connectionPromise = showConnectFlow
      ? services.getConnection(clinicBrandId)
      : Promise.resolve(null)
    Promise.all([services.getConfig(clinicBrandId), connectionPromise]).then(([cfg, conn]) => {
      if (!active) return
      if (cfg?.ok) {
        setReviewUrl(cfg.googleReviewUrl || '')
        setPlaceId(cfg.googlePlaceId || '')
      }
      setConnected(!!conn?.connected)
      setLoading(false)
    })
    return () => {
      active = false
    }
  }, [clinicBrandId, services, showConnectFlow])

  const urlValid = !reviewUrl.trim() || /^https?:\/\//i.test(reviewUrl.trim())

  const handleSave = useCallback(async () => {
    if (!clinicBrandId) return
    if (!urlValid) {
      toast.error('The review link must start with http:// or https://')
      return
    }
    setSaving(true)
    const res = await services.saveConfig(clinicBrandId, reviewUrl.trim(), placeId.trim())
    setSaving(false)
    if (res?.ok) {
      setReviewUrl(res.googleReviewUrl || '')
      setPlaceId(res.googlePlaceId || '')
      toast.success('Google Business settings saved.')
    } else {
      toast.error(res?.message || 'Unable to save Google Business settings.')
    }
  }, [clinicBrandId, reviewUrl, placeId, urlValid, services])

  const handleConnect = useCallback(() => {
    if (!clinicBrandId) return
    if (!getGoogleBusinessClientId()) {
      toast.error('Google sign-in is not configured yet (missing Google OAuth client).')
      return
    }
    const redirectUri = getGoogleBusinessRedirectUri()
    setConnecting(true)
    popupRef.current = window.open(
      buildGoogleBusinessAuthUrl(clinicBrandId),
      'gb-oauth',
      'width=520,height=660,menubar=no,toolbar=no',
    )

    const onMessage = async (event) => {
      if (event.origin !== window.location.origin) return
      if (event?.data?.type !== 'google-business-oauth') return
      window.removeEventListener('message', onMessage)
      const { code, error } = event.data
      if (error || !code) {
        setConnecting(false)
        if (error) toast.error('Google sign-in was cancelled.')
        return
      }
      const res = await services.connect(clinicBrandId, code, redirectUri)
      setConnecting(false)
      if (res?.ok) {
        setConnected(true)
        toast.success('Connected to Google Business Profile.')
        loadLocations()
      } else {
        toast.error(res?.message || 'Unable to connect Google Business.')
      }
    }
    window.addEventListener('message', onMessage)
  }, [clinicBrandId, loadLocations, services])

  const handleSelectLocation = useCallback(
    async (locationName) => {
      if (!locationName) return
      setSavingLocation(true)
      const res = await services.selectLocation(clinicBrandId, locationName)
      setSavingLocation(false)
      if (res?.ok) {
        setReviewUrl(res.googleReviewUrl || '')
        setPlaceId(res.googlePlaceId || '')
        toast.success('Google location saved.')
      } else {
        toast.error(res?.message || 'Unable to save the selected location.')
      }
    },
    [clinicBrandId, services],
  )

  const handleDisconnect = useCallback(async () => {
    const res = await services.disconnect(clinicBrandId)
    if (res?.ok) {
      setConnected(false)
      setLocations([])
      toast.success('Disconnected from Google.')
    } else {
      toast.error(res?.message || 'Unable to disconnect.')
    }
  }, [clinicBrandId, services])

  const effectiveLink =
    reviewUrl.trim() ||
    (placeId.trim() ? `https://search.google.com/local/writereview?placeid=${placeId.trim()}` : '')

  return (
    <Box sx={{ maxWidth: 620, ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <RateReviewOutlinedIcon sx={{ color: INDIGO }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>Google Business</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Set your clinic&apos;s Google review link so practitioners can send patients a &ldquo;Send
        Google Review Link&rdquo; request after a visit.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Loading…
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {showConnectFlow && (
            <>
              {/* Connect (OAuth) flow */}
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  bgcolor: '#f8fafc',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        Google Business Profile
                      </Typography>
                      {connected && (
                        <Chip
                          icon={<CheckCircleRoundedIcon />}
                          label="Connected"
                          size="small"
                          sx={{
                            bgcolor: 'rgba(22,163,74,0.12)',
                            color: '#16a34a',
                            fontWeight: 700,
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Auto-fetch your location and review link from Google.
                    </Typography>
                  </Box>
                  {connected ? (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        onClick={loadLocations}
                        disabled={loadingLocations}
                        sx={{ textTransform: 'none' }}
                      >
                        {loadingLocations ? 'Loading…' : 'Choose location'}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={handleDisconnect}
                        sx={{ textTransform: 'none' }}
                      >
                        Disconnect
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={connecting ? <CircularProgress size={16} /> : <GoogleIcon />}
                      onClick={handleConnect}
                      disabled={connecting}
                      sx={{ textTransform: 'none' }}
                    >
                      {connecting ? 'Connecting…' : 'Connect Google'}
                    </Button>
                  )}
                </Box>

                {connected && locations.length > 0 && (
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Your Google location"
                    sx={{ mt: 1.5 }}
                    defaultValue=""
                    onChange={(e) => handleSelectLocation(e.target.value)}
                    disabled={savingLocation}
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.name} value={loc.name}>
                        {loc.title || loc.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Box>

              <Divider>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  or enter manually
                </Typography>
              </Divider>
            </>
          )}

          <TextField
            label="Google review link"
            placeholder="https://g.page/r/…/review"
            value={reviewUrl}
            onChange={(e) => setReviewUrl(e.target.value)}
            fullWidth
            size="small"
            error={!urlValid}
            helperText={
              !urlValid
                ? 'Must start with http:// or https://'
                : 'The direct “write a review” link for your clinic.'
            }
            InputProps={{ startAdornment: <InputAdornment position="start">🔗</InputAdornment> }}
          />
          <TextField
            label="Google Place ID (optional)"
            placeholder="ChIJ…"
            value={placeId}
            onChange={(e) => setPlaceId(e.target.value)}
            fullWidth
            size="small"
            helperText="If the link is blank, we build the review link from this Place ID."
          />

          {effectiveLink && (
            <Alert severity="info" sx={{ wordBreak: 'break-all' }}>
              Patients will be sent:{' '}
              <Link href={effectiveLink} target="_blank" rel="noopener noreferrer">
                {effectiveLink}
              </Link>
            </Alert>
          )}

          <Box>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !clinicBrandId}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none', bgcolor: INDIGO, '&:hover': { bgcolor: '#3730a3' } }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  )
}
